import { z } from "zod";

import {
    agentActorSchema,
    serializedAgentErrorSchema,
} from "./common.contract.ts";
import { agentPublicEventSchema } from "./event.contract.ts";

export const agentStreamFrameMaxBytes = 1_000_000;
export const agentTransportIdentifierSchema = z.string().min(1).max(256);
export const agentTransportSubjectSchema = z.string().min(1).max(512);

export const agentSessionPermissionSchema = z.enum([
    "snapshot",
    "events",
    "submit",
    "delete",
]);

export const agentAccessTokenClaimsSchema = z
    .object({
        schemaVersion: z.literal(1),
        tokenId: agentTransportIdentifierSchema,
        sessionId: agentTransportSubjectSchema,
        actorId: agentTransportSubjectSchema,
        actorRole: agentActorSchema.shape.role,
        permissions: z
            .array(agentSessionPermissionSchema)
            .min(1)
            .refine(
                (permissions) =>
                    new Set(permissions).size === permissions.length,
                { message: "Permissions must be unique" },
            ),
        issuedAt: z.string().datetime({ offset: true }),
        expiresAt: z.string().datetime({ offset: true }),
    })
    .strict();

export const agentStreamCapabilityClaimsSchema = z
    .object({
        schemaVersion: z.literal(1),
        capabilityId: agentTransportIdentifierSchema,
        sessionId: agentTransportSubjectSchema,
        actorId: agentTransportSubjectSchema,
        permissions: z
            .array(agentSessionPermissionSchema)
            .min(1)
            .refine(
                (permissions) =>
                    permissions.includes("events") &&
                    new Set(permissions).size === permissions.length,
                {
                    message:
                        "Stream capability requires unique event permission",
                },
            ),
        afterCursor: z.number().int().nonnegative(),
        connectionNonce: z.string().min(16).max(256),
        issuedAt: z.string().datetime({ offset: true }),

        expiresAt: z.string().datetime({ offset: true }),

        streamExpiresAt: z.string().datetime({ offset: true }),
    })
    .strict();

export const createAgentAccessTokenRequestSchema = z
    .object({
        sessionId: agentTransportSubjectSchema,
        actorId: agentTransportSubjectSchema,
        actorRole: agentActorSchema.shape.role,
        permissions: z
            .array(agentSessionPermissionSchema)
            .min(1)
            .refine(
                (permissions) =>
                    new Set(permissions).size === permissions.length,
                { message: "Permissions must be unique" },
            ),
        expiresInMs: z.number().int().positive().max(86_400_000),
    })
    .strict();

export const createAgentAccessTokenResponseSchema = z
    .object({
        token: z.string().min(1),
        expiresAt: z.string().datetime({ offset: true }),
    })
    .strict();

export const createStreamCapabilityRequestSchema = z
    .object({
        sessionId: agentTransportSubjectSchema,
        permissions: z
            .array(agentSessionPermissionSchema)
            .min(1)
            .refine(
                (permissions) =>
                    permissions.includes("events") &&
                    new Set(permissions).size === permissions.length,
                {
                    message:
                        "Stream capability requires unique event permission",
                },
            ),
        afterCursor: z.number().int().nonnegative(),
        connectionNonce: z.string().min(16).max(256),
    })
    .strict();

export const createStreamCapabilityResponseSchema = z
    .object({
        capability: z.string().min(1),
        protocol: z.string().min(1),
        expiresAt: z.string().datetime({ offset: true }),
    })
    .strict();

export const agentServerStreamFrameSchema = z.discriminatedUnion("type", [
    z
        .object({
            type: z.literal("stream.opened"),
            protocolVersion: z.literal(1),
            sessionId: agentTransportSubjectSchema,
            acceptedAfterCursor: z.number().int().nonnegative(),
            headCursor: z.number().int().nonnegative(),
            expiresAt: z.string().datetime({ offset: true }),
            acknowledgementWindow: z.number().int().positive().max(1_000),
        })
        .strict(),
    z
        .object({
            type: z.literal("events.batch"),
            firstCursor: z.number().int().positive(),
            lastCursor: z.number().int().positive(),
            events: z.array(agentPublicEventSchema).min(1).max(500),
        })
        .strict()
        .superRefine((frame, context) => {
            if (
                new TextEncoder().encode(JSON.stringify(frame)).byteLength >
                agentStreamFrameMaxBytes
            ) {
                context.addIssue({
                    code: "custom",
                    message: "Event batch exceeds one megabyte",
                });
            }
            if (
                frame.events[0]?.cursor !== frame.firstCursor ||
                frame.events.at(-1)?.cursor !== frame.lastCursor
            ) {
                context.addIssue({
                    code: "custom",
                    path: ["events"],
                    message: "Event cursors must match batch bounds",
                });
            }
            for (let index = 1; index < frame.events.length; index += 1) {
                if (
                    frame.events[index]?.cursor !==
                    (frame.events[index - 1]?.cursor ?? 0) + 1
                ) {
                    context.addIssue({
                        code: "custom",
                        path: ["events", index, "cursor"],
                        message: "Event batch cursors must be contiguous",
                    });
                }
            }
        }),
    z
        .object({
            type: z.literal("stream.error"),
            error: serializedAgentErrorSchema,
            terminal: z.boolean(),
        })
        .strict(),
]);

export const agentClientStreamFrameSchema = z
    .object({
        type: z.literal("stream.ack"),
        cursor: z.number().int().nonnegative(),
    })
    .strict();

export const agentTransportErrorResponseSchema = z
    .object({ error: serializedAgentErrorSchema })
    .strict();

export type AgentClientStreamFrame = z.infer<
    typeof agentClientStreamFrameSchema
>;
export type AgentAccessTokenClaims = z.infer<
    typeof agentAccessTokenClaimsSchema
>;
export type AgentServerStreamFrame = z.infer<
    typeof agentServerStreamFrameSchema
>;
export type AgentSessionPermission = z.infer<
    typeof agentSessionPermissionSchema
>;
export type AgentStreamCapabilityClaims = z.infer<
    typeof agentStreamCapabilityClaimsSchema
>;
export type CreateAgentAccessTokenRequest = z.infer<
    typeof createAgentAccessTokenRequestSchema
>;
export type CreateAgentAccessTokenResponse = z.infer<
    typeof createAgentAccessTokenResponseSchema
>;
export type {
    default as IWorkerTransportInternalAuthorizer,
    WorkerTransportInternalAction,
} from "./worker-transport-internal-authorizer.interface.ts";
export type { default as IWorkerTransportTokenCodec } from "./worker-transport-token-codec.interface.ts";
